import { Pipe, PipeTransform, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EnvironmentService } from '@galaxy/core';
import { TranslateService } from '@ngx-translate/core';
import { ConversationChannel, MessageType, ParticipantType } from '@vendasta/conversation';
import { ChatSourceId } from '@vendasta/galaxy/chat-composer';
import { DEFAULT_OPENAI_BOT_NAME, DEFAULT_WEBCHAT_BOT_NAME } from '../../core/src/lib/inbox.constants';
import { ConversationMessage, Media } from '../../core/src/lib/interface/conversation.interface';
import { CONVERSATION_HOST_APP_INTERFACE_TOKEN, USER_ID_TOKEN } from '../../core/src/lib/tokens';
import { MessageGroup } from '../../core/src/lib/types';

// returns true if message is a System Message type
@Pipe({ name: 'isSystemMessage' })
export class IsSystemMessagePipe implements PipeTransform {
  transform(message: ConversationMessage): boolean {
    return message?.type === MessageType.MESSAGE_TYPE_SYSTEM;
  }
}

// Determine whether we need to specify message type as 'sent' or 'received'
enum MessageStatus {
  Sent = 'sent',
  Received = 'received',
}
@Pipe({ name: 'messageStatusType' })
export class MessageStatusType implements PipeTransform {
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);

  transform(message: ConversationMessage): MessageStatus {
    return this.hostAppInterface.isSenderFromOrganization(message) ? MessageStatus.Sent : MessageStatus.Received;
  }
}

@Pipe({ name: 'messageFrom' })
export class MessageFrom implements PipeTransform {
  constructor(public translateService: TranslateService) {}
  transform(message: ConversationMessage): string {
    return messageFrom(message);
  }
}

@Pipe({ name: 'messageVia' })
export class MessageVia implements PipeTransform {
  constructor(public translateService: TranslateService) {}
  transform(message: ConversationMessage): string {
    switch (message.channel) {
      case ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS:
        return 'INBOX.CHAT.VIA_GOOGLE';
      case ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK:
        return 'INBOX.CHAT.VIA_FACEBOOK';
      case ConversationChannel.CONVERSATION_CHANNEL_SMS:
        return 'INBOX.CHAT.VIA_SMS';
      case ConversationChannel.CONVERSATION_CHANNEL_EMAIL:
        return 'INBOX.CHAT.VIA_EMAIL';
      case ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT:
        return 'INBOX.CHAT.VIA_WEBCHAT';
      default:
        return null;
    }
  }
}

@Pipe({ name: 'messageCodeBacktick' })
export class MessageCodeBacktickPipe implements PipeTransform {
  transform(message: string): string {
    if (!message) {
      return '';
    }

    const blockPattern = /```(.+?)?\n([\s\S]*?)```/g;
    const inlinePattern = /`([^`]+)`/g;
    const blockFormatted = message.replace(blockPattern, '<pre><code>$2</code></pre>');
    const finalFormat = blockFormatted.replace(inlinePattern, '<code>$1</code>');
    return finalFormat;
  }
}

@Pipe({ name: 'getMessageMediaUrl' })
export class GetMessageMediaUrlPipe implements PipeTransform {
  constructor(public environmentService: EnvironmentService) {}
  transform(media: Media): string {
    const env = this.environmentService.getEnvironmentString();
    return (
      media?.mediaUrl || `https://storage.googleapis.com/conversations-${env}-shared-files/${media.mediaLocationPath}`
    );
  }
}

@Pipe({ name: 'isFromYou' })
export class IsFromYouPipe implements PipeTransform {
  private readonly userId = toSignal(inject(USER_ID_TOKEN));
  transform(messageGroup: MessageGroup): boolean {
    return isFromYou(messageGroup, this.userId());
  }
}

@Pipe({ name: 'chatSourceIconName' })
export class ChatSourceIconName implements PipeTransform {
  transform(channel: ConversationChannel): ChatSourceId | undefined {
    switch (channel) {
      case ConversationChannel.CONVERSATION_CHANNEL_INTERNAL:
        return undefined;
      case ConversationChannel.CONVERSATION_CHANNEL_GOOGLE_BUSINESS_COMMUNICATIONS:
        return 'google';
      case ConversationChannel.CONVERSATION_CHANNEL_FACEBOOK:
        return 'facebook';
      case ConversationChannel.CONVERSATION_CHANNEL_SMS:
        return 'sms';
      case ConversationChannel.CONVERSATION_CHANNEL_EMAIL:
        return 'email';
      case ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT:
        return 'webchat';
      case ConversationChannel.CONVERSATION_CHANNEL_OPENAI:
        return undefined;
      default:
        throw new Error('unsupported channel ' + channel);
    }
  }
}

@Pipe({ name: 'chatProfileIcon' })
export class ChatProfileIconPipe implements PipeTransform {
  private readonly userId = toSignal(inject(USER_ID_TOKEN));
  transform(messageGroup: MessageGroup): string {
    const unknownContact = !messageFrom(messageGroup.lastMessage);
    const notYou = !isFromYou(messageGroup, this.userId());
    return unknownContact && notYou ? 'person' : '';
  }
}

function messageFrom(message: ConversationMessage): string | undefined {
  if (message.sender?.participantType === ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT) {
    return message.channel === ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT
      ? DEFAULT_WEBCHAT_BOT_NAME
      : DEFAULT_OPENAI_BOT_NAME;
  }
  return message?.sender?.name;
}

function isFromYou(messageGroup: MessageGroup, userId: string | undefined): boolean {
  const senderID: string | undefined = messageGroup?.sender?.internalParticipantId;
  if (!senderID) return false;
  return senderID === userId;
}
